import React, { useEffect, useMemo, useCallback, useRef, useState } from 'react'
import { useTranslation } from 'react-i18next'
import { Header } from 'components/common'
import axios from 'axios'
import { getBenefitsWidgetConfig, getVendorCode } from '../benefitsWidgetConfig'
import { displayToast } from 'actions/Dashboard'
import { useAppDispatch } from 'hooks'
import { LoadingSVG } from 'assets/svg'

// Declarations for external widget global, comes from benefits-widget.js
declare const benefits: {
  init: (accessToken: string) => void
}

// Type for the cardIdentifierGenerated event detail
interface CardIdentifierEventDetail {
  cardNumberId: string
}

interface Step1Props {
  onNext: (cardNumberId: string) => void
}

export const Step1 = ({ onNext }: Step1Props) => {
  const widgetTokenRef = useRef<string | null>(null)
  const [isCheckingEligibility, setIsCheckingEligibility] = useState(false)

  const { t } = useTranslation('pub', {
    keyPrefix: 'PublicRoutes.signUp',
  })

  const dispatch = useAppDispatch()

  const showWidgetFailedError = useCallback(() => {
    dispatch(
      displayToast({
        infoType: 'danger',
        title: t('verificationError', 'Verification Error'),
        message: t(
          'verificationStepError',
          'We’re having trouble loading the verification step. Please try again later.',
        ),
      }),
    )
  }, [dispatch, t])

  const handleEligibilityChecks = useCallback(
    async (id: string) => {
      setIsCheckingEligibility(true)
      try {
        const [benefitResponse, productResponse, usedResponse] =
          await Promise.all([
            axios.post(
              '/api/mastercard-benefits/benefit-search',
              { cardNumberId: id },
              { baseURL: process.env.REACT_APP_AUTH_URL },
            ),
            axios.post(
              '/api/mastercard-benefits/product-search',
              { cardNumberId: id },
              { baseURL: process.env.REACT_APP_AUTH_URL },
            ),
            axios.get(`/api/mcr/benefits-eligibility/used/${id}`, {
              baseURL: process.env.REACT_APP_AUTH_URL,
              validateStatus: () => true,
            }),
          ])

        const benefitOk =
          benefitResponse.status === 200 &&
          benefitResponse.data?.benefitEligible === true
        const productOk =
          productResponse.status === 200 &&
          productResponse.data?.productEligible === true
        const usedOk =
          usedResponse.status === 200 && usedResponse.data?.used === false

        if (benefitOk && productOk && usedOk) {
          dispatch(
            displayToast({
              infoType: 'success',
              title: t('eligibilityVerified', 'Eligibility Verified'),
            }),
          )
          onNext(id) // move cardNumberId to step 2
          return
        }

        if (!benefitOk && usedOk) {
          dispatch(
            displayToast({
              infoType: 'danger',
              title: t('eligibilityFailed', 'Eligibility Check Failed'),
              message: t(
                'notEligibleForBenefit',
                'This card number is eligible for this card benefit. Please contact your bank',
              ),
            }),
          )
          return
        }

        if (!usedOk) {
          dispatch(
            displayToast({
              infoType: 'danger',
              title: t('eligibilityBenefitsUsed', 'Benefit already activated'),
              message: t(
                'eligibilityBenefitsAlreadyUsed',
                'This card has already been used to access this benefit. Log in to the My Cyber Risk portal to view your company’s cyber risk.',
              ),
            }),
          )
          return
        }

        dispatch(
          displayToast({
            infoType: 'danger',
            title: t('eligibilityFailed', 'Eligibility Check Failed'),
            message: t(
              'eligibilityChecksFailed',
              'Unable to verify your card eligibility. Please try again.',
            ),
          }),
        )
      } catch (err: any) {
        dispatch(
          displayToast({
            infoType: 'danger',
            title: t('eligibilityFailed', 'Eligibility Check Failed'),
            message: t(
              'eligibilityChecksError',
              'An error occurred while verifying your card. Please try again or contact support.',
            ),
          }),
        )
      } finally {
        setIsCheckingEligibility(false)
      }
    },
    [dispatch, onNext, setIsCheckingEligibility, t],
  )

  useEffect(() => {
    let mounted = true
    let script: HTMLScriptElement | null = null
    const controller = new AbortController()

    const loadWidget = async () => {
      try {
        const { data } = await axios.get(
          '/api/mastercard-benefits/widget-token',
          {
            baseURL: process.env.REACT_APP_AUTH_URL,
            signal: controller.signal,
          },
        )

        const accessToken = data?.accessToken

        if (!mounted) {
          return
        }

        if (!accessToken) {
          console.error('[Step1] widget token missing from response')
          showWidgetFailedError()
          return
        }
        widgetTokenRef.current = accessToken
        script = document.createElement('script')
        if (process.env.REACT_APP_ENVIRONMENT === 'production') {
          script.src =
            'https://benefits.mastercard.com/widgets/benefits-widget.js'
        } else {
          script.src =
            'https://mtf.benefits.mastercard.com/widgets/benefits-widget.js'
        }
        script.async = true
        script.referrerPolicy = 'strict-origin-when-cross-origin'

        script.onload = () => {
          if (!mounted) {
            return
          }
          const token = widgetTokenRef.current
          if (!token) {
            showWidgetFailedError()
            return
          }
          benefits.init(token)
        }

        script.onerror = () => {
          if (mounted) showWidgetFailedError()
        }

        document.body.appendChild(script)
      } catch (widgetError) {
        console.error('Widget token request failed')
        showWidgetFailedError()
      }
    }

    loadWidget()

    return () => {
      mounted = false
      controller.abort()
      if (script && document.body.contains(script)) {
        document.body.removeChild(script)
      }
    }
  }, [showWidgetFailedError])

  useEffect(() => {
    const benefitsSearchElement = document.querySelector('benefits-search')

    if (!benefitsSearchElement) {
      return
    }

    const handleCardIdentifierGenerated = (event: Event) => {
      const customEvent = event as CustomEvent<CardIdentifierEventDetail>
      const { cardNumberId: id } = customEvent.detail

      if (id) {
        handleEligibilityChecks(id)
      } else {
        console.error(
          'cardIdentifierGenerated event received without cardNumberId',
        )
        dispatch(
          displayToast({
            infoType: 'danger',
            title: t('eligibilityFailed', 'Eligibility Check Failed'),
            message: t(
              'eligibilityChecksFailed',
              'Unable to verify your card eligibility. Please try again.',
            ),
          }),
        )
      }
    }

    const handleApiRequestFailed = () => {
      console.error('Widget API request failed')
      dispatch(
        displayToast({
          infoType: 'danger',
          title: t('eligibilityFailed', 'Eligibility Check Failed'),
          message: t(
            'eligibilityChecksFailed',
            'Unable to verify your card eligibility. Please try again.',
          ),
        }),
      )
    }

    benefitsSearchElement.addEventListener(
      'cardIdentifierGenerated',
      handleCardIdentifierGenerated,
    )
    benefitsSearchElement.addEventListener(
      'apiRequestFailed',
      handleApiRequestFailed,
    )

    return () => {
      benefitsSearchElement.removeEventListener(
        'cardIdentifierGenerated',
        handleCardIdentifierGenerated,
      )
      benefitsSearchElement.removeEventListener(
        'apiRequestFailed',
        handleApiRequestFailed,
      )
    }
  }, [dispatch, handleEligibilityChecks, t])

  const benefitsWidgetData = useMemo(() => {
    const vendorCode = getVendorCode(process.env.REACT_APP_ENVIRONMENT)
    return JSON.stringify(getBenefitsWidgetConfig(vendorCode, t))
  }, [t])

  return (
    <div className="form-step">
      <Header as="h3">
        {t('verifyEligibility', 'Verify your Eligibility')}
      </Header>
      <div className="onboard-message">
        {t(
          'verifyEligibilityDesc',
          'Enter your card number to verify your access to this complementary benefit.',
        )}
      </div>
      <div className="card-verification-placeholder">
        <div style={{ position: 'relative' }}>
          <div
            style={{
              filter: isCheckingEligibility ? 'blur(2px)' : 'none',
              pointerEvents: isCheckingEligibility ? 'none' : 'auto',
            }}
          >
            <benefits-search data={benefitsWidgetData}></benefits-search>
          </div>
          {isCheckingEligibility && (
            <div
              style={{
                position: 'absolute',
                top: 0,
                left: 0,
                right: 0,
                bottom: 0,
                display: 'flex',
                flexDirection: 'column',
                alignItems: 'center',
                justifyContent: 'center',
                background: 'rgba(255, 255, 255, 0.7)',
                zIndex: 1,
                gap: '8px',
              }}
            >
              <LoadingSVG size="32" />
              <span>{t('checking', 'Checking...')}</span>
            </div>
          )}
        </div>
        <p>
          {t(
            'cardNumberInfo',
            "Verification only. Your card won't be charged or stored",
          )}
        </p>
      </div>
    </div>
  )
}
